import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UsFlag from './flags/UsFlag';
import EsFlag from './flags/EsFlag';
import BrFlag from './flags/BrFlag';

const LANGUAGES = [
  { code: 'en', labelKey: 'language.en', Flag: UsFlag },
  { code: 'es', labelKey: 'language.es', Flag: EsFlag },
  { code: 'pt', labelKey: 'language.pt', Flag: BrFlag },
];

const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);

  const activeLanguage =
    LANGUAGES.find((language) => language.code === i18n.resolvedLanguage) ?? LANGUAGES[0];

  const closeAndRefocus = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  /** Arrow keys cycle focus through the menu items. */
  const handleMenuKeyDown = (event, index) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (index + offset + LANGUAGES.length) % LANGUAGES.length;
    itemRefs.current[nextIndex]?.focus();
  };

  const selectLanguage = (code) => {
    i18n.changeLanguage(code);
    closeAndRefocus();
  };

  const ActiveFlag = activeLanguage.Flag;

  return (
    <div ref={containerRef} className="relative flex-shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('language.label')}
        className="flex items-center gap-2 rounded-full border border-main border-opacity-40 px-3 py-1.5 text-white transition hover:border-main hover:text-main"
      >
        <ActiveFlag className="w-5 h-auto rounded-sm" />
        <span className="text-[14px] font-[500]">{activeLanguage.code.toUpperCase()}</span>
        <svg width="10" height="7" viewBox="0 0 21 13" fill="none" aria-hidden="true">
          <path d="M20.3987 2.1394L10.3987 12.1394L0.398682 2.1394L2.17368 0.364404L10.3987 8.5894L18.6237 0.364404L20.3987 2.1394Z" fill="currentColor" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label={t('language.label')}
          className="absolute right-0 top-full mt-2 w-[170px] rounded-lg border border-main border-opacity-40 bg-black py-1 z-[9999999]"
        >
          {LANGUAGES.map((language, index) => {
            const { Flag } = language;
            const isActive = language.code === activeLanguage.code;
            return (
              <button
                key={language.code}
                ref={(node) => { itemRefs.current[index] = node; }}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => selectLanguage(language.code)}
                onKeyDown={(event) => handleMenuKeyDown(event, index)}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-[14px] transition hover:text-main ${
                  isActive ? 'text-main' : 'text-white'
                }`}
              >
                <Flag className="w-5 h-auto rounded-sm" />
                {t(language.labelKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
